"use client";

import { useFormContext } from "react-hook-form";
import { PlusCircle, Trash2 } from "lucide-react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { InvestmentAssetForm } from "./investment-asset-form";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";

export function StepAssetsLiabilities() {
  const { control, watch, setValue } = useFormContext();
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  const otherLoans = watch("liabilities.otherLoans") || [];

  const addLoan = () => {
    setValue("liabilities.otherLoans", [
      ...otherLoans,
      { name: "", amount: 0, period: 0, interestRate: 0 },
    ]);
  };

  const removeLoan = (index: number) => {
    setValue(
      "liabilities.otherLoans",
      otherLoans.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">
        {t("lifePlan.assetsLiabilities.title")}
      </h2>

      <Tabs defaultValue="assets" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="assets">
            {t("lifePlan.assetsLiabilities.assets.title")}
          </TabsTrigger>
          <TabsTrigger value="liabilities">
            {t("lifePlan.assetsLiabilities.liabilities.title")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <FormField
                control={control}
                name="assets.savings.amount"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>
                        {t("lifePlan.assetsLiabilities.assets.savings")}
                      </FormLabel>
                    </div>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={0}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">{currency}</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="assets.savings.return"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("lifePlan.assetsLiabilities.assets.return")}
                    </FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={-100}
                          max={100}
                          step={0.1}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">%</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="assets.savings.volatility"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("lifePlan.assetsLiabilities.assets.volatility")}
                    </FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          min={0}
                          max={100}
                          step={0.1}
                          value={field.value}
                          onValueChange={(val) => field.onChange(val ?? 0)}
                          ref={field.ref}
                          name={field.name}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <span className="text-sm">%</span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <h3 className="text-lg font-medium mt-6">
                {t("lifePlan.assetsLiabilities.assets.investmentAssets")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InvestmentAssetForm
                  control={control}
                  name="assets.stocks"
                  label={t("lifePlan.assetsLiabilities.assets.stocks")}
                />

                <InvestmentAssetForm
                  control={control}
                  name="assets.bonds"
                  label={t("lifePlan.assetsLiabilities.assets.bonds")}
                />

                <InvestmentAssetForm
                  control={control}
                  name="assets.mutualFunds"
                  label={t("lifePlan.assetsLiabilities.assets.mutualFunds")}
                />

                <InvestmentAssetForm
                  control={control}
                  name="assets.insurance"
                  label={t("lifePlan.assetsLiabilities.assets.insurance")}
                />

                <InvestmentAssetForm
                  control={control}
                  name="assets.other"
                  label={t("lifePlan.assetsLiabilities.assets.other")}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="liabilities" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-lg font-medium">
                {t("lifePlan.assetsLiabilities.liabilities.housingLoan")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="liabilities.housingLoan.0.amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.assetsLiabilities.liabilities.amount")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">{currency}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="liabilities.housingLoan.0.period"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("lifePlan.assetsLiabilities.liabilities.period")}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            max={50}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">{t("common.years")}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="liabilities.housingLoan.0.interestRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "lifePlan.assetsLiabilities.liabilities.interestRate"
                        )}
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            min={0}
                            step={0.1}
                            value={field.value}
                            onValueChange={(val) => field.onChange(val ?? 0)}
                            ref={field.ref}
                            name={field.name}
                            onBlur={field.onBlur}
                          />
                        </FormControl>
                        <span className="text-sm">%</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">
                    {t("lifePlan.assetsLiabilities.liabilities.otherLoans")}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLoan}
                    className="h-8 px-2 text-xs"
                  >
                    <PlusCircle className="mr-1 h-3.5 w-3.5" />
                    {t("lifePlan.assetsLiabilities.liabilities.addLoan")}
                  </Button>
                </div>

                {otherLoans.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("lifePlan.assetsLiabilities.liabilities.noLoans")}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {otherLoans.map((_: any, index: number) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 relative"
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLoan(index)}
                          className="absolute top-2 right-2 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">
                            {t(
                              "lifePlan.assetsLiabilities.liabilities.removeLoan"
                            )}
                          </span>
                        </Button>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            control={control}
                            name={`liabilities.otherLoans.${index}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "lifePlan.assetsLiabilities.liabilities.name"
                                  )}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder={t(
                                      "lifePlan.assetsLiabilities.liabilities.carLoan"
                                    )}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`liabilities.otherLoans.${index}.amount`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "lifePlan.assetsLiabilities.liabilities.amount"
                                  )}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">{currency}</span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`liabilities.otherLoans.${index}.period`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "lifePlan.assetsLiabilities.liabilities.period"
                                  )}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      max={30}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">
                                    {t("common.years")}
                                  </span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`liabilities.otherLoans.${index}.interestRate`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  {t(
                                    "lifePlan.assetsLiabilities.liabilities.interestRate"
                                  )}
                                </FormLabel>
                                <div className="flex items-center gap-2">
                                  <FormControl>
                                    <NumberInput
                                      min={0}
                                      step={0.1}
                                      value={field.value}
                                      onValueChange={(val) => field.onChange(val ?? 0)}
                                      ref={field.ref}
                                      name={field.name}
                                      onBlur={field.onBlur}
                                    />
                                  </FormControl>
                                  <span className="text-sm">%</span>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
